import { GuestReceiverControlManager, GuestTelemetrySource, GuestTuningEndpoint, GuestTrialLifecycle,
  TuningChannelService, parseTuningWireMessage } from '@playcastdotio/PeerCore';
import type { GuestBrowserCapability, GuestPeerSessionOptions, GuestTrialMediaPort, IGuestTelemetryObserver,
  IPeerConnection, ITuningClock, TuningCapabilities, TuningCommand, TuningIdentity, TuningReceipt,
  TuningTelemetryWindow, TuningTrialControlContext, TuningWireMessage } from '@playcastdotio/PeerCore';
import type { TuningExperimentControl, TuningExperimentSnapshot } from '@playcastdotio/PeerCore';
import { BrowserSchedulingMonitor } from '../telemetry/BrowserSchedulingMonitor';
import { GuestTrialState } from './GuestTrialState';
import { GuestTrialNegotiation } from './GuestTrialNegotiation';

type Options = { eligible: boolean; buildId: string; runtimeId: string; clock: ITuningClock;
  localPeerId: () => string; capability?: GuestBrowserCapability; isMobile: boolean;
  sampling: (intervalMs: number) => void; media: GuestTrialMediaPort;
  firstFrameReady: () => boolean;
  fallback: (message: TuningWireMessage, context: TuningTrialControlContext) => Promise<void> };
type Snapshot = { armed: boolean; guest: TuningCapabilities; host?: TuningCapabilities;
  target?: TuningIdentity; leaseId?: string; latest?: TuningTelemetryWindow; latestGuest?: TuningTelemetryWindow;
  latestHost?: TuningTelemetryWindow; receipt?: TuningReceipt; experiment?: TuningExperimentSnapshot };
type LatestRvfcObservation = Parameters<IGuestTelemetryObserver['observeFrame']>[0];

/** Survives explicit media replacement; authenticated identity and input stay in the page. */
export class GuestTrialBinding {
  readonly state: GuestTrialState;
  readonly telemetry: GuestTelemetrySource;
  readonly negotiation = new GuestTrialNegotiation();
  private readonly receivers = new GuestReceiverControlManager();
  private readonly lifecycle: GuestTrialLifecycle;
  private readonly scheduling: BrowserSchedulingMonitor;
  private endpoint?: GuestTuningEndpoint;
  private channel?: TuningChannelService;
  private connection?: IPeerConnection;
  private generation = 0;
  private localEpoch = 0;
  private trackSeen = false;
  private firstComposition?: LatestRvfcObservation;
  private readonly firstListeners = new Set<() => void>();
  private readonly listeners = new Set<() => void>();
  private host?: TuningCapabilities;
  private latest?: TuningTelemetryWindow;
  private latestGuest?: TuningTelemetryWindow;
  private latestHost?: TuningTelemetryWindow;
  private experiment?: TuningExperimentSnapshot;
  private replacing = false;
  private readonly receiverReceipts = new Map<string, import('@playcastdotio/PeerCore').TuningSettingReceipt>();
  private readonly rejectedReasons = new Set<string>();
  private receipt?: TuningReceipt;
  private expiryTimer?: ReturnType<typeof setTimeout>;
  constructor(private readonly options: Options) {
    this.state = new GuestTrialState(options.clock, options.localPeerId);
    this.telemetry = new GuestTelemetrySource({ clock: options.clock, state: this.state, capability: options.capability,
      isMobile: options.isMobile, scheduler: { configureTuningSampling: options.sampling } });
    this.scheduling = new BrowserSchedulingMonitor(options.clock, this.telemetry);
    this.lifecycle = new GuestTrialLifecycle({ clock: options.clock, media: options.media,
      authorize: context => this.state.authorize(context), emit: message => this.emit(message) });
    this.telemetry.subscribe(window => { this.latest = window; this.latestGuest = window; this.channel?.send({ type: 'stream-tuning', version: 1, kind: 'telemetry', payload: window }); this.changed(); });
  }
  hooks(): Pick<GuestPeerSessionOptions, 'telemetryObserver' | 'tuningConnectionObserver' | 'tuningReceiverObserver' | 'tuningSdpTransform' | 'tuningReceiverHintOwned'> {
    const observer: IGuestTelemetryObserver = { observeReport: sample => this.telemetry.observeReport(sample),
      observeFrame: frame => { this.telemetry.observeFrame(frame); if (this.trackSeen && this.options.firstFrameReady() && !this.firstComposition) {
        this.firstComposition = frame; for (const notify of this.firstListeners) notify(); } },
      observeSkippedSample: epoch => this.telemetry.observeSkippedSample(epoch),
      resetForConnection: epoch => { this.localEpoch = epoch; this.trackSeen = false; this.firstComposition = undefined; this.receiverReceipts.clear();
        this.telemetry.setCalibrationClock({ state: 'uncalibrated' }); this.telemetry.resetForConnection(epoch); }, dispose: () => undefined };
    return { telemetryObserver: observer, tuningConnectionObserver: async (connection, epoch) => this.attach(connection, epoch),
      tuningReceiverObserver: (connection, epoch) => this.receiversAvailable(connection, epoch),
      tuningSdpTransform: sdp => this.negotiation.transform(sdp), tuningReceiverHintOwned: (kind, property) => {
        if (!this.state.armed()) return false;
        const key = `receiver.${kind}${property === 'jitterBufferTarget' ? 'JitterBufferTargetMs' : 'PlayoutDelayHintMs'}` as const;
        return this.receivers.owns(key);
      } };
  }
  capabilities(): TuningCapabilities {
    if (this.endpoint) return { ...this.endpoint.capabilities(), armed: this.state.armed() };
    return { protocolVersion: 1, eligible: this.options.eligible, armed: false, endpoint: 'guest',
      buildId: this.options.buildId, runtimeId: this.options.runtimeId, settings: this.negotiation.capabilities(),
      strategies: ['production', 'required-keyframes', 'loss-repair', 'minimal-instrumentation'] };
  }
  snapshot(): Snapshot { return { armed: this.state.armed(), guest: this.capabilities(), host: this.host,
    target: this.state.armed() ? this.state.snapshot().target : undefined, leaseId: this.state.leaseId(), latest: this.latest,
    latestGuest: this.latestGuest, latestHost: this.latestHost, receipt: this.receipt, experiment: this.experiment }; }
  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  signalingContext(): { version: 1; sessionId: string; trialId: string; connectionEpoch: number; commandId: string; leaseId: string; configurationHash: string } | undefined {
    const context = this.state.trialContext();
    return context ? { version: 1, sessionId: context.target.sessionId, trialId: context.target.trialId,
      connectionEpoch: context.target.connectionEpoch, commandId: context.commandId, leaseId: context.leaseId, configurationHash: context.configurationHash } : undefined;
  }
  async receive(value: unknown): Promise<boolean> {
    const message = parseTuningWireMessage(value); if (!message || !this.authorized(message)) return false;
    if (message.kind === 'hello') this.host = message.payload;
    else if (message.kind === 'session') this.grant(message.payload);
    else if (message.kind === 'command' && this.endpoint) {
      const receipt = await this.endpoint.apply(message.payload); this.state.commit(receipt);
      await this.emit({ type: 'stream-tuning', version: 1, kind: 'receipt', payload: receipt });
      if (message.payload.action.kind === 'stop') { this.scheduling.stop(); this.state.stop(); }
    } else if (message.kind === 'receipt') { this.receipt = message.payload; this.state.commit(message.payload, true); }
    else if (message.kind === 'telemetry') {
      this.latest = message.payload; this.latestHost = message.payload;
      const clock = { ...message.payload.clock };
      if (clock.state === 'calibrated' && typeof clock.offsetMs === 'number' && typeof clock.ageMs === 'number') {
        clock.ageMs += Math.max(0, this.options.clock.wallMs() + clock.offsetMs - message.payload.wallAnchorMs);
      }
      this.telemetry.setCalibrationClock(clock);
    }
    else if (message.kind === 'experiment-state') this.experiment = message.payload.snapshot;
    else if (message.kind === 'prepare') {
      try { this.negotiation.preflight(message.payload.resolvedConfiguration, this.connection?.getRtcPeerConnection() ?? null); }
      catch (error) {
        const { resolvedConfiguration: _configuration, ...context } = message.payload;
        await this.emit({ type: 'stream-tuning', version: 1, kind: 'prepared', payload: { ...context, accepted: false, settings: [],
          error: { code: 'unsupported-configuration', message: (error as Error).message } } }); return true;
      }
      this.state.prepared(message.payload); await this.lifecycle.prepare(message.payload);
    }
    else if (message.kind === 'commit') { this.replacing = true; try { await this.lifecycle.activate(message.payload); } finally { this.replacing = false; } }
    else if (message.kind === 'abort') await this.lifecycle.abort(message.payload, message.payload.reason);
    this.changed(); return true;
  }
  sendCommand(command: TuningCommand): boolean {
    return !!this.channel && this.state.validate(command).valid && this.channel.send({ type: 'stream-tuning', version: 1, kind: 'command', payload: command }) !== 'rejected';
  }
  sendExperiment(control: TuningExperimentControl): boolean {
    const message: TuningWireMessage = { type: 'stream-tuning', version: 1, kind: 'experiment-command', payload: control };
    return !!this.channel && this.authorized(message) && this.channel.send(message) !== 'rejected';
  }
  exportSnapshot(): unknown {
    const { leaseId: _lease, ...snapshot } = this.snapshot();
    return { snapshot, trial: this.state.armed() ? this.state.snapshot() : undefined, incident: this.telemetry.incident() };
  }
  firstFrame(target: TuningIdentity, epoch: number, startedAtMs: number): Promise<{ guestConnectionStartMonotonicMs: number; guestCompositionMonotonicMs: number; guestEndpointConnectionEpoch: number }> {
    return new Promise((resolve, reject) => {
      const generation = this.generation;
      const finish = () => {
        if (generation !== this.generation || epoch !== this.localEpoch || target.trialId !== this.state.snapshot().target.trialId) return;
        const frame = this.firstComposition;
        if (frame && typeof frame.callbackMonotonicMs === 'number' && frame.callbackMonotonicMs >= startedAtMs) { cleanup(); resolve({ guestConnectionStartMonotonicMs: startedAtMs,
          guestCompositionMonotonicMs: frame.callbackMonotonicMs, guestEndpointConnectionEpoch: epoch }); }
      };
      const timeout = setTimeout(() => { cleanup(); reject(new Error('No new-generation composition callback')); }, 10_000);
      const cleanup = () => { clearTimeout(timeout); this.firstListeners.delete(finish); };
      this.firstListeners.add(finish); finish();
    });
  }
  endpointEpoch(): number { return this.localEpoch; }
  isReplacing(): boolean { return this.replacing; }
  settingReceipts() { return [...this.negotiation.settings(), ...this.receiverReceipts.values()]; }
  dispose(): void {
    this.generation++; this.channel?.dispose(); this.lifecycle.dispose(); this.endpoint?.dispose();
    this.scheduling.dispose(); this.telemetry.dispose(); this.receivers.dispose(); this.state.close();
    if (this.expiryTimer) clearTimeout(this.expiryTimer); this.listeners.clear(); this.firstListeners.clear();
  }
  private attach(connection: IPeerConnection, epoch: number): void {
    if (!this.options.eligible) return;
    const generation = ++this.generation; this.channel?.dispose(); this.connection = connection; this.localEpoch = epoch;
    this.rejectedReasons.clear();
    this.receivers.attach(null); const native = connection.getRtcPeerConnection();
    if (!native) {
      console.warn('[StreamTuning.channel]', { endpoint: 'guest', reason: 'native-rtc-unavailable', endpointEpoch: epoch });
      return;
    }
    this.negotiation.apply(native);
    this.channel = new TuningChannelService({ role: 'guest', connection, clock: this.options.clock,
      capabilities: () => this.capabilities(), isCurrent: () => generation === this.generation && connection === this.connection,
      authorize: message => this.authorized(message), onMessage: message => { void this.receive(message).catch(error => console.warn('[StreamTuning] Guest control failed', error)); },
      onRejected: reason => { if (!this.rejectedReasons.has(reason)) { this.rejectedReasons.add(reason);
        console.warn('[StreamTuning.channel]', { endpoint: 'guest', reason, endpointEpoch: epoch }); } } });
  }
  private receiversAvailable(connection: IPeerConnection, epoch: number): void {
    if (connection !== this.connection || epoch !== this.localEpoch) return;
    this.trackSeen = true; this.receivers.attach(connection.getRtcPeerConnection());
    if (this.state.armed()) {
      for (const [key, value] of Object.entries(this.state.snapshot().effective)) if (key.startsWith('receiver.'))
        this.receiverReceipts.set(key, this.receivers.apply(key as Parameters<GuestReceiverControlManager['apply']>[0], value));
      this.ready();
    }
  }
  private grant(grant: Parameters<GuestTrialState['accept']>[0]): void {
    if (!this.state.accept(grant)) return;
    const capabilities = { ...this.capabilities(), armed: true };
    this.endpoint ??= new GuestTuningEndpoint({ state: this.state, authority: this.state, telemetry: this.telemetry, receivers: this.receivers, capabilities });
    this.telemetry.setEndpointMetadata({ buildId: this.options.buildId, runtimeId: this.options.runtimeId, configurationHash: this.state.snapshot().configurationHash });
    const settings = this.state.snapshot().effective;
    this.telemetry.configureFrameSampling(this.state.snapshot().strategyId !== 'minimal-instrumentation');
    this.telemetry.configure({ windowMs: typeof settings['telemetry.windowMs'] === 'number' ? settings['telemetry.windowMs'] : 1_000,
      statsIntervalMs: typeof settings['telemetry.statsIntervalMs'] === 'number' ? settings['telemetry.statsIntervalMs'] : 1_000 });
    this.telemetry.arm(true);
    if (this.state.snapshot().strategyId === 'minimal-instrumentation') this.scheduling.stop(); else this.scheduling.start();
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = setTimeout(() => this.expire(), grant.leaseTtlMs);
    this.ready();
  }
  private expire(): void {
    this.telemetry.arm(false); this.scheduling.stop();
    const settings = this.receivers.restore();
    if (settings.some(setting => setting.status === 'failed')) {
      this.receipt = { version: 1, commandId: `lease-expiry-${this.generation}`, target: { ...this.state.snapshot().target },
        stage: 'failed', settings, policyVersion: this.options.buildId,
        error: { code: 'receiver-restore-failed', message: 'Lease expired, but production receiver hint restoration failed' } };
      this.state.commit(this.receipt); console.warn('[StreamTuning] Guest lease cleanup failed', this.receipt.error);
    }
    this.changed();
  }
  private ready(): void {
    const leaseId = this.state.leaseId(); if (!leaseId) return;
    this.channel?.send({ type: 'stream-tuning', version: 1, kind: 'session-ready', payload: {
      target: this.state.snapshot().target, leaseId, capabilities: this.capabilities() } });
  }
  private authorized(message: TuningWireMessage): boolean {
    if (!this.options.eligible) return false;
    if (message.kind === 'hello') return message.payload.endpoint === 'host';
    if (message.kind === 'session') return message.payload.target.peerId === this.options.localPeerId();
    if (message.kind === 'prepare') return this.state.authorizePreparation(message.payload);
    if (['commit', 'abort', 'prepared', 'resume'].includes(message.kind)) return this.state.authorize(message.payload as TuningTrialControlContext);
    if (message.kind === 'command') return this.state.validate(message.payload).valid;
    if (message.kind === 'receipt' && (this.state.acceptsCleanupReceipt(message.payload) || this.state.acceptsAuthoritativeReceipt(message.payload))) return true;
    if (!this.state.armed()) return false;
    if (message.kind === 'experiment-command') {
      const control = message.payload, context = control.action === 'start' ? control.request : control;
      return context.sessionId === this.state.snapshot().target.sessionId && context.leaseId === this.state.leaseId();
    }
    if (message.kind === 'experiment-state') return message.payload.sessionId === this.state.snapshot().target.sessionId;
    if (message.kind === 'telemetry') {
      const target = this.state.snapshot().target, incoming = message.payload.target;
      return incoming.sessionId === target.sessionId && incoming.peerId === target.peerId && incoming.trialId === target.trialId
        && incoming.connectionEpoch === target.connectionEpoch && incoming.configRevision === target.configRevision;
    }
    return true;
  }
  private async emit(message: TuningWireMessage): Promise<void> {
    if (this.channel?.send(message) === 'sent') return;
    const context = 'target' in message.payload && 'commandId' in message.payload ? message.payload as TuningTrialControlContext : undefined;
    if (context && ['prepare', 'prepared', 'commit', 'resume', 'abort'].includes(message.kind)) return this.options.fallback(message, context);
    if (!this.channel) throw new Error('Tuning control channel is unavailable');
  }
  private changed(): void { for (const listener of this.listeners) listener(); }
}
